/**
 * <pcard-sibling-swatches>
 *
 * Colour dots on a collection/search product card that link between sibling
 * colour products (each colour is a separate Shopify product, not a native
 * variant). Matches Princess Polly's card behaviour: clicking a dot swaps the
 * card's main image and title in place instead of navigating, and moves a
 * ring highlight to the clicked dot.
 *
 * Every dot is a real <a href="..."> to that colour's product page, so this
 * is progressive enhancement: without JS (or before it loads) the dots still
 * navigate normally. With JS, clicks on a non-current dot are intercepted.
 *
 * The card's Quick Add control (see 'pcard-quick-add-tab' / 'pcard-quick-add-
 * panel') is rebuilt to match whichever colour is now showing: its sizes,
 * availability and variant ids are re-fetched from that colour's own product
 * JSON, so a shopper who swaps colour and then quick-adds gets the colour
 * they're actually looking at -- not whichever colour the card first loaded
 * with. The "Choose options" fallback link (multi-option products) gets its
 * href swapped the same way as the image/title links, for the same reason.
 *
 * The card's badges are re-synced the same way: "Sold out", "Sale" and
 * "🔥 Trending" are all rendered once server-side for whichever colour the
 * card first loaded with, so without this they'd keep showing (or keep NOT
 * showing) that original colour's stock/price/tag status no matter which
 * sibling colour a shopper swaps to -- e.g. a card that first loaded on a
 * sold-out colour would still read "Sold out" after swapping to a colour
 * that's actually in stock, or a colour that's on sale would lose its "Sale"
 * badge after swapping to one that isn't. Each badge keeps the exact visual
 * position the theme already gives it (Sold out / Sale in the shared
 * top-left .product-card__badge container, Trending in its own
 * bottom-right .product-card__badge--trending container) -- only their
 * content and presence change, never their location.
 */
(function () {
  if (customElements.get('pcard-sibling-swatches')) return;

  class PcardSiblingSwatches extends HTMLElement {
    connectedCallback() {
      this.swapToken = 0;
      this.addEventListener('click', this.onClick);
      this.ensureTrendingBadgeStyles();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const link = event.target.closest('a[data-swap-url]');
      if (!link || !this.contains(link)) return;

      // Always intercept: even the already-current dot should not "navigate"
      // to the page it's already showing. stopPropagation guards against any
      // ancestor click handler (analytics, a "whole card is a link" pattern
      // added by an app) treating this same click as a card-level navigation.
      event.preventDefault();
      event.stopPropagation();

      if (link.classList.contains('is-current')) return;

      const card = this.closest('.product-card');
      if (!card) return;

      const swapUrl = link.getAttribute('data-swap-url');
      const swapTitle = link.getAttribute('data-swap-title');
      const swapImage = link.getAttribute('data-swap-image');
      if (!swapUrl) return;

      this.swapImage(card, swapUrl, swapImage, swapTitle);
      this.swapTitle(card, swapUrl, swapTitle);
      this.swapActionsLink(card, swapUrl, swapTitle);
      this.setCurrent(link);
      this.updateQuickAdd(card, swapUrl);
    }

    swapImage(card, url, imageSrc, title) {
      const wrapperLinks = card.querySelectorAll('.product-card__image-wrapper > a');
      wrapperLinks.forEach((a) => {
        a.setAttribute('href', url);
        if (a.hasAttribute('aria-label') && title) {
          a.setAttribute('aria-label', title);
        }
      });

      if (!imageSrc) return;

      // The theme fades the main image out and a "second" hover-preview image
      // in whenever the pointer is anywhere inside .product-card__wrapper --
      // which includes this swatch row, not just the photo itself. So while
      // the shopper's cursor is still over a dot, the (unswapped) second image
      // sits on top and hides the change until they move away and hover ends.
      // Swapping both images together means whichever one is visible always
      // shows the newly picked colour, with no "wait, it didn't change" gap.
      const images = card.querySelectorAll(
        '.product-card__image--main img, .product-card__image--second img'
      );
      images.forEach((img) => {
        // srcset/sizes take priority over src in the browser's image
        // selection, so they must be cleared before setting a plain src or
        // the swap won't visibly take effect.
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.setAttribute('src', imageSrc);
        if (title) img.setAttribute('alt', title);
      });
    }

    swapTitle(card, url, title) {
      if (!title) return;
      const titleLink = card.querySelector('.product-card__title a');
      if (titleLink) {
        titleLink.textContent = title;
        titleLink.setAttribute('href', url);
      }
    }

    swapActionsLink(card, url, title) {
      // The "Choose options" fallback (multi-option products, which don't get
      // a Quick Add panel) links straight to the product page -- point it at
      // the newly picked colour too, same reasoning as the image/title links.
      const actionsLink = card.querySelector('.product-card__main-actions .product-card__atc');
      if (!actionsLink) return;
      actionsLink.setAttribute('href', url);
      if (title) actionsLink.setAttribute('aria-label', title);
    }

    setCurrent(activeLink) {
      this.querySelectorAll('a[data-swap-url]').forEach((a) => {
        const isActive = a === activeLink;
        a.classList.toggle('is-current', isActive);
        if (isActive) {
          a.setAttribute('aria-current', 'true');
        } else {
          a.removeAttribute('aria-current');
        }
      });
    }

    // The theme only links component-trending-badge.css (which positions
    // .product-card__badge--trending bottom-right, pill-shaped, instead of
    // the shared top-left spot) on a page where at least one SERVER-RENDERED
    // card already has the trending tag -- see card-product.liquid's
    // `{%- if has_trending_tag -%}{{ 'component-trending-badge.css' | asset_url | stylesheet_tag }}{%- endif -%}`.
    // A shopper who swaps to a trending sibling on a page where nothing
    // started out trending would otherwise get an unstyled badge in the
    // wrong spot. Injecting the same rules here once (idempotent; harmless
    // to duplicate if the real stylesheet also loaded, since the values are
    // identical) guarantees the trending badge always lands in its correct
    // location regardless of what the page started with.
    ensureTrendingBadgeStyles() {
      if (document.getElementById('pcard-trending-badge-fallback-style')) return;
      const style = document.createElement('style');
      style.id = 'pcard-trending-badge-fallback-style';
      style.textContent =
        '.product-card__badge--trending{inset-inline-start:auto;inset-inline-end:0;top:auto;bottom:1.2rem;}' +
        '.product-card__badge--trending .f-badge{border-radius:999px 0 0 999px;padding-inline:1rem .8rem;}' +
        '.f-badge--icon{gap:.4rem;background:rgba(20,20,20,.7);backdrop-filter:blur(3px);-webkit-backdrop-filter:blur(3px);color:#fff;}' +
        '.f-badge__icon{font-size:1.2rem;line-height:1;flex-shrink:0;}';
      document.head.appendChild(style);
    }

    // Adds/removes/updates the "Sold out" and "Sale" badges on `card` to
    // match `product`/`variant`'s own stock and price data. Both live in the
    // same top-left .product-card__badge container (theme.css), sold-out
    // first then sale, matching card-product.liquid's own render order.
    updateSoldOutAndSaleBadges(card, wrapper, product, variant) {
      let container = card.querySelector('.product-card__badge:not(.product-card__badge--trending)');
      let soldOutBadge = container ? container.querySelector('.f-badge--soldout') : null;
      let saleBadge = container ? container.querySelector('.f-badge--sale') : null;

      const isSoldOut = product.available === false;
      const showSaleBadge = this.dataset.showSaleBadge === 'true';
      const compareAtPrice = variant ? Number(variant.compare_at_price) || 0 : 0;
      const price = variant ? Number(variant.price) || 0 : 0;
      const isOnSale = showSaleBadge && !isSoldOut && compareAtPrice > price;

      if (!container && (isSoldOut || isOnSale)) {
        container = document.createElement('div');
        container.className = 'product-card__badge';
        wrapper.appendChild(container);
      }

      if (isSoldOut) {
        if (!soldOutBadge) {
          // Copy the badge markup the theme already rendered elsewhere on
          // the page (any other sold-out card) rather than hard-coding the
          // label, so this stays correct if the store's translated
          // "Sold out" text ever changes. Falls back to plain English on
          // the rare page that has no sold-out card to copy from at all.
          const existingTemplate = document.querySelector('.f-badge--soldout');
          soldOutBadge = document.createElement('span');
          soldOutBadge.className = 'f-badge f-badge--soldout';
          soldOutBadge.setAttribute('aria-hidden', 'true');
          soldOutBadge.textContent = existingTemplate ? existingTemplate.textContent : 'Sold out';
          container.insertBefore(soldOutBadge, container.firstChild);
        }
      } else if (soldOutBadge) {
        soldOutBadge.remove();
        soldOutBadge = null;
      }

      if (isOnSale) {
        if (!saleBadge) {
          saleBadge = document.createElement('span');
          saleBadge.className = 'f-badge f-badge--sale';
          saleBadge.setAttribute('aria-hidden', 'true');
          // Sold-out renders before sale when both are present; sale is the
          // first (only) badge otherwise -- same order as the server render.
          if (soldOutBadge) {
            container.insertBefore(saleBadge, soldOutBadge.nextSibling);
          } else {
            container.insertBefore(saleBadge, container.firstChild);
          }
        }
        saleBadge.innerHTML = this.computeSaleBadgeHtml(compareAtPrice, price);
      } else if (saleBadge) {
        saleBadge.remove();
      }

      // Liquid only renders .product-card__badge at all when it has
      // something to show (sold-out / sale / a regular tag__ badge) --
      // match that here so a swap to a colour with neither doesn't leave an
      // empty positioned div behind. (Any regular tag__ badge the page
      // rendered server-side for THIS card at load time is left alone --
      // it isn't stock/price dependent and this swap doesn't touch it.)
      if (container && container.children.length === 0) {
        container.remove();
      }
    }

    // Builds the sale badge's inner HTML the same way
    // settings.pcard_sale_badge_type drives it server-side in
    // card-product.liquid, given prices in cents (as returned by the
    // product's .js endpoint).
    computeSaleBadgeHtml(compareAtPrice, price) {
      if (compareAtPrice <= price) return '';

      const badgeType = this.dataset.saleBadgeType;

      if (badgeType === 'show_percentage') {
        // Liquid: amount = (compare_at_price - price) * 100 / compare_at_price
        // (integer division, truncated), then the badge shows amount * -1,
        // e.g. "-20%".
        const amount = Math.trunc(((compareAtPrice - price) * 100) / compareAtPrice);
        return amount * -1 + '%';
      }

      if (badgeType === 'show_amount') {
        const diff = compareAtPrice - price;
        const formatted =
          window.FoxTheme && window.FoxTheme.Currency && window.FoxTheme.settings
            ? window.FoxTheme.Currency.formatMoney(diff, window.FoxTheme.settings.moneyFormat)
            : '$' + (diff / 100).toFixed(2);
        // Mirrors locale key products.product.price.save_price_html:
        // "Save <span data-sale-value>{{ amount }}</span>"
        return 'Save <span data-sale-value>' + formatted + '</span>';
      }

      // Mirrors locale key products.product.on_sale: "Sale"
      return 'Sale';
    }

    // Adds or removes the "🔥 Trending" badge on `card` to match `product`'s
    // own tags (a product is trending when any tag contains 'tag__hot_', per
    // card-product.liquid). Lives in its own bottom-right container, kept
    // separate from the sold-out/sale container above so its position never
    // moves.
    updateTrendingBadge(card, wrapper, product) {
      const tags = (product && product.tags) || [];
      let trendingTag = null;
      for (let i = 0; i < tags.length; i++) {
        if (tags[i].indexOf('tag__hot_') !== -1) {
          trendingTag = tags[i];
          break;
        }
      }

      let container = card.querySelector('.product-card__badge--trending');

      if (!trendingTag) {
        if (container) container.remove();
        return;
      }

      // Mirrors Liquid: tag "tag__hot_new" -> split on '__', take the last
      // part ("hot_new") -> split that on '_', take the last part ("new").
      const afterDoubleUnderscore = trendingTag.split('__').pop();
      const trendingName = afterDoubleUnderscore.split('_').pop();

      if (!container) {
        container = document.createElement('div');
        container.className = 'product-card__badge product-card__badge--trending';
        const badge = document.createElement('span');
        badge.className = 'f-badge f-badge--hot f-badge--icon';
        const icon = document.createElement('span');
        icon.className = 'f-badge__icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '\u{1F525}';
        badge.appendChild(icon);
        container.appendChild(badge);
        wrapper.appendChild(container);
      }

      const badge = container.querySelector('.f-badge--hot');
      const icon = badge.querySelector('.f-badge__icon');
      // Rebuild the text node right after the icon, matching Liquid's
      // `{{- trending_name -}}` sitting right after it, without disturbing
      // the icon span itself.
      while (icon.nextSibling) icon.nextSibling.remove();
      badge.appendChild(document.createTextNode(trendingName));
    }

    updateBadge(card, product, variant) {
      const wrapper = card.querySelector('.product-card__image-wrapper');
      if (!wrapper) return;

      this.updateSoldOutAndSaleBadges(card, wrapper, product, variant);
      this.updateTrendingBadge(card, wrapper, product);
    }

    async updateQuickAdd(card, url) {
      const panel = card.querySelector('.product-card__quick-add-panel');
      if (!panel) return; // this card has no Quick Add (multi-option fallback) -- nothing to sync

      // Close the panel AND disable the tab itself for the duration of the
      // fetch below. Closing alone isn't enough: a shopper (or, as testing
      // found, a fast script) can reopen the panel with a click before the
      // new colour's data has landed, and the sizes sitting there a moment
      // ago still belong to the OLD colour and are still perfectly clickable
      // -- that's exactly how the wrong colour ends up in the cart. Disabling
      // the tab makes that window impossible to hit rather than just
      // unlikely to hit.
      const quickAdd = card.querySelector('pcard-quick-add');
      const tabButton = card.querySelector('.product-card__quick-add-tab');
      if (quickAdd && typeof quickAdd.setExpanded === 'function') {
        quickAdd.setExpanded(false);
      }
      if (tabButton) tabButton.disabled = true;

      const token = ++this.swapToken;

      let product;
      try {
        // `url` is the dot's real product-page href, which on a search or
        // collection page carries Shopify's own tracking query string (e.g.
        // "?_pos=2&_sid=...&_ss=r"). Appending ".js" straight onto that
        // lands inside the query string instead of on the path, so the
        // request silently comes back as the normal HTML product page, not
        // JSON -- response.json() below then throws, this whole fetch is
        // treated as a network failure, and the panel is left showing the
        // PREVIOUS colour's (still fully clickable) sizes. That's exactly
        // how the wrong colour ended up in the cart. Stripping the query
        // string first is what makes this hit the real JSON endpoint.
        const productUrl = url.split('?')[0];
        const response = await fetch(productUrl + '.js');
        if (!response.ok) throw new Error('Quick Add product fetch failed: ' + response.status);
        product = await response.json();
      } catch (error) {
        // Network hiccup: leave the panel as-is rather than risk building it
        // from a partial response. Re-enable the tab so the shopper isn't
        // stuck -- worst case it reopens showing the previous colour's sizes,
        // which is stale but was never wrong at the time it was fetched.
        if (token === this.swapToken && tabButton) tabButton.disabled = false;
        return;
      }

      // A newer swap started while this fetch was in flight -- let that one
      // win (it will re-enable the tab itself once IT resolves) and drop
      // this now-stale result on the floor.
      if (token !== this.swapToken) return;

      if (tabButton) tabButton.disabled = false;

      const variants = product && product.variants ? product.variants : [];
      const defaultVariant = variants.length ? variants.find((v) => v.available) || variants[0] : null;

      this.updateBadge(card, product, defaultVariant);

      const label = panel.querySelector('.product-card__quick-add-panel-label');
      const list = panel.querySelector('.product-card__quick-add-sizes');
      const form = panel.querySelector('form');
      const hiddenIdInput = form ? form.querySelector('input[name="id"]') : null;
      if (!label || !list || !hiddenIdInput || variants.length === 0) return;

      const isSingleVariant = variants.length === 1;
      label.textContent = isSingleVariant ? 'Add to My Cart' : 'Select a Size';

      list.textContent = '';
      variants.forEach((variant) => {
        const valueLabel = isSingleVariant ? 'One Size' : variant.option1;

        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-card__quick-add-size';
        button.setAttribute('data-variant-id', variant.id);
        if (!variant.available) {
          button.disabled = true;
          button.setAttribute('aria-label', valueLabel + ' - Sold out');
        } else {
          button.setAttribute('aria-label', valueLabel);
        }

        const span = document.createElement('span');
        span.textContent = valueLabel;
        button.appendChild(span);

        const spinnerWrap = document.createElement('div');
        spinnerWrap.className = 'loading__spinner hidden';
        spinnerWrap.innerHTML =
          '<svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
          '<circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>' +
          '</svg>';
        button.appendChild(spinnerWrap);

        li.appendChild(button);
        list.appendChild(li);
      });

      hiddenIdInput.value = defaultVariant.id;
    }
  }

  customElements.define('pcard-sibling-swatches', PcardSiblingSwatches);
})();
